import { D, F, G, S } from "@mobily/ts-belt";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import { fetcher } from "../../hooks";

export const defaultFilter = {
  order: "ra",
  type: {},
  franchise: {},
  category: {},
  cursor: "",
  limit: 24,
  name: "",
};

const keys = new Set(Object.keys(defaultFilter));

const parseValue = (key, value) => {
  if (key !== "category") return value;

  return Object.entries(value).flatMap(([categoryId, subcategories]) => {
    if (!G.isObject(subcategories)) return [categoryId];

    const subIds = Object.entries(subcategories)
      .filter(([, has]) => has)
      .map(([subcategoryId]) => `${categoryId}.${subcategoryId}`);

    return [categoryId, ...subIds];
  }, []);
};

export const removeEmptyFilterKeys = (filter) =>
  Object.entries(filter).reduce((result, [key, value]) => {
    if (keys.has(key) && !F.equals(value, defaultFilter[key])) {
      result[key] = parseValue(key, value);
    }
    return result;
  }, {});

const defaultFilters = {
  sort: [
    { id: "ra", label: "Most Popular" },
    { id: "ed", label: "Upcoming Events" },
    { id: "na", label: "Name: A-Z" },
    { id: "nd", label: "Name: Z-A" },
  ],
  shopTypes: [],
  franchises: [],
  categories: [],
};

const defaultPagination = {
  totalResults: 0,
  currentResults: 0,
  pageSize: 24,
  hasNext: false,
  nextCursor: null,
};

function isValidOrder(order) {
  return (
    G.isString(order) &&
    S.isNotEmpty(order) &&
    defaultFilters.sort.some((filter) => filter.id === order)
  );
}

function isValidNumber(num) {
  return G.isNumber(num) && !Number.isNaN(num) && num >= 0;
}

const keyMap = {
  order: "o",
  type: "ty",
  franchise: "f",
  category: "g",
  cursor: "c",
  limit: "l",
  name: "q",
};

const formatters = {
  g(categories) {
    return Object.entries(categories).map(([categoryId, subcategories]) => {
      const subcategoryIds = Object.keys(subcategories).filter(
        (key) => subcategories[key],
      );
      return `${categoryId}.${subcategoryIds.join(".")}`;
    });
  },
};

function toMap(acc, value) {
  acc[value] = true;
  return acc;
}

const paramMap = {
  o: {
    name: "order",
    parser: (value) => (isValidOrder(value) ? value : "ra"),
  },
  ty: {
    name: "type",
    parser: (value) => value.split(",").reduce(toMap, {}),
  },
  f: {
    name: "franchise",
    parser: (value) => value.split(",").reduce(toMap, {}),
  },
  g: {
    name: "category",
    parser: (param) =>
      param.split(",").reduce((result, category) => {
        const [categoryId, ...rest] = category.split(".");

        result[categoryId] = rest.reduce((acc, subcategoryId) => {
          acc[subcategoryId] = true;
          return acc;
        }, {});

        return result;
      }, {}),
  },
  c: {
    name: "cursor",
    parser: (value) => value,
  },
  l: {
    name: "limit",
    parser: (value) => (isValidNumber(Number(value)) ? Number(value) : 24),
  },
  q: {
    name: "name",
    parser: (value) => value,
  },
};

function parseDefaultsFromParams(search) {
  const params = new URLSearchParams(search);

  if (params.size === 0) {
    return defaultFilter;
  }

  const result = {};

  for (const [key, value] of params.entries()) {
    const mappedKey = paramMap[key];

    if (!mappedKey) {
      continue;
    }

    const { name, parser } = mappedKey;

    const parsedValue = parser(value);

    result[name] = parsedValue;
  }

  return D.merge(defaultFilter, result);
}

export function useFilterParams() {
  const location = useLocation();
  const history = useHistory();

  const [filter, setFilter] = React.useState(() =>
    parseDefaultsFromParams(location.search),
  );

  const updateFilterAndSetParams = React.useCallback(
    (updateFnOrFilter) => {
      let updateFn = updateFnOrFilter;

      if (typeof updateFn !== "function") {
        updateFn = () => updateFnOrFilter;
      }

      setFilter((filter) => {
        const newFilter = updateFn(filter);

        const search = Object.entries(newFilter)
          .reduce((result, [key, value]) => {
            const mappedKey = keyMap[key];

            const formatter = formatters[mappedKey] ?? F.identity;
            const formattedValue = formatter(value);

            if (G.isString(formattedValue)) {
              result.set(mappedKey, formattedValue);
            } else if (G.isNumber(formattedValue)) {
              result.set(mappedKey, String(formattedValue));
            } else if (G.isObject(formattedValue)) {
              const values = Object.keys(formattedValue).filter(
                (key) => formattedValue[key],
              );

              if (values.length > 0) {
                result.set(mappedKey, values.join(","));
              }
            } else if (G.isArray(formattedValue)) {
              if (formattedValue.length > 0) {
                result.set(mappedKey, formattedValue.join(","));
              }
            }

            return result;
          }, new URLSearchParams())
          .toString();

        history.replace({ search });

        return newFilter;
      });
    },
    [history],
  );

  return { filter, setFilter: updateFilterAndSetParams };
}

function hasName(name) {
  return G.isString(name) && S.isNotEmpty(name);
}

function hasCursor(cursor) {
  return G.isString(cursor) && S.isNotEmpty(cursor);
}

function paramsFor({ franchise, category, name, order, type, cursor }) {
  const params = new URLSearchParams({
    order,
  });

  if (hasName(name)) {
    params.set("name", name);
  }

  if (type) {
    const typeArr = Object.keys(type).filter((key) => type[key]);
    if (typeArr.length > 0) {
      params.set("type", typeArr.join(","));
    }
  }

  if (franchise) {
    const franchiseArr = Object.keys(franchise).filter((key) => franchise[key]);
    if (franchiseArr.length > 0) {
      params.set("franchise", franchiseArr.join(","));
    }
  }

  if (category) {
    const formattedValue = formatters.g(category);
    if (formattedValue.length > 0) {
      params.set("category", formattedValue.join(","));
    }
  }

  if (hasCursor(cursor)) {
    params.set("cursor", cursor);
  }

  return params;
}

export function useShops(filter = defaultFilter) {
  const { data, error, isValidating, mutate } = useSWR(
    `${import.meta.env.VITE_DISCOVER_API_ENDPOINT}/v1/browse/shops?${paramsFor(
      filter,
    )}`,
    fetcher,
    { keepPreviousData: true },
  );

  const pagination = React.useMemo(() => {
    return data?.pagination || defaultPagination;
  }, [data]);

  return {
    error,
    isError: Boolean(error),
    isLoading: !data && !error,
    isValidating,
    mutate,
    shops: data?.shops || [],
    pagination,
  };
}

const BROWSE_SHOPS_FILTERS_URL = `${
  import.meta.env.VITE_S3_CLOUDFRONT_URL
}/filters/browse-shops-filters.json`;

export function useFilters() {
  const { data, error, isValidating, mutate } = useSWR(
    BROWSE_SHOPS_FILTERS_URL,
    fetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 900_000,
    },
  );

  const filters = React.useMemo(() => {
    return data || defaultFilters;
  }, [data]);

  return {
    error,
    filters,
    isError: Boolean(error),
    isLoading: !data && !error,
    isValidating,
    mutate,
  };
}
